import React, {useMemo} from 'react';
import {twMerge} from 'tailwind-merge';

import {BackButton} from '../components/BackButton';
import {SettingsContentWrapper} from '../components/SettingsContentWrapper';
import {SettingToggleRow} from '../components/SettingToggleRow';
import {SelectedSettingType} from '../types';
import {useWorkspaceSettingsContext} from '../WorkspaceSettingsContextProvider';
import {getWorkspaceLayoutSettingWithDefaults} from './getWorkspaceLayoutSettings';
import {WorkspaceLayoutAutoOrganizePrefix} from './WorkspaceLayoutAutoOrganizePrefix';
import {WorkspaceMode} from './WorkspaceMode';

export const WorkspaceLayoutSettings = ({
  setSelectedSettingType,
}: {
  setSelectedSettingType: (type: SelectedSettingType | undefined) => void;
}) => {
  const {
    workspaceLayout,
    updateWorkspaceLayoutSettings,
    updateLayoutSettingAndSortPanels,
    trackWorkspaceSettingChange,
  } = useWorkspaceSettingsContext();
  const {showEmptySections, sortAlphabetically} = useMemo(
    () => getWorkspaceLayoutSettingWithDefaults(workspaceLayout),
    [workspaceLayout]
  );

  return (
    <SettingsContentWrapper
      title="Workspace layout"
      description="Define the overall structure of this workspace, including sectioning logic and panel organization."
      header={<BackButton onClick={() => setSelectedSettingType(undefined)} />}>
      <div
        className={twMerge(
          'border-t-[1px] border-solid border-moon-250',
          'mt-12 gap-20'
        )}
      />
      <div
        data-test="workspace-layout-settings"
        className={twMerge('flex flex-col', 'gap-20 py-16', 'h-full w-full')}>
        <WorkspaceMode />
        <SettingToggleRow
          displayValue={!showEmptySections}
          label="Hide empty sections during search"
          onCheckedChange={(newValue: boolean) => {
            // Important: we need to flip the value to reflect label text
            updateWorkspaceLayoutSettings({showEmptySections: !newValue});
            trackWorkspaceSettingChange(
              'showEmptySections',
              newValue.toString()
            );
          }}
        />
        <SettingToggleRow
          displayValue={sortAlphabetically}
          label="Sort panels alphabetically"
          onCheckedChange={(newValue: boolean) => {
            updateLayoutSettingAndSortPanels(newValue);
          }}
        />
        <WorkspaceLayoutAutoOrganizePrefix />
      </div>
    </SettingsContentWrapper>
  );
};
